import { useMemo } from 'react';
import {
  CompetitionGenderEnum,
  EventCompetitivenessEnum,
  type FindPlayerAvailabilityFieldsFragment,
  type GeographyComparisonExp,
  PlaySessionMatchCompetitivenessEnum,
  TeamTypesEnum,
  VenueAccessTypesEnum,
  VenueNetsEnum,
  useGetFindPlayersQuery,
} from 'types/generated/client';
import { getVerificationLevel } from 'types/verification';
import { buildGameTitle } from 'utils/shared/games/buildGameTitle';
import { isUuidFormatValid } from 'utils/shared/string/isUuidFormatValid';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { getAccessParamters } from 'utils/shared/venues';
import { useViewer } from 'hooks/useViewer';
import { Weekday } from 'screens/ListAvailability/types';
import { formatShortTime, weekdayShortLabel } from 'screens/ListAvailability/utils';
import { deriveCourtTypes, timeOfDayFromHour } from './filters';
import { type LatLng, distanceMilesTo } from './geo';
import type {
  FindCourtType,
  FindPlayer,
  FindPlayerAvailabilitySlot,
  FindPlayerNeighborhood,
  FindPlayerRequestVenue,
  FindPlayerVenue,
  FindVenueDetails,
  PlayerGender,
} from './types';

type PlayerAvailabilityRow = FindPlayerAvailabilityFieldsFragment;
type PlayerLocationRow = PlayerAvailabilityRow['venues'][number];
type PlayerVenueRow = NonNullable<PlayerLocationRow['venue']>;

/** A preferred location: a venues row, or a custom court the player typed in. */
interface PlayerLocation {
  id: string;
  name: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  venue: PlayerVenueRow | null;
}

const toPlayerLocation = (row: PlayerLocationRow): PlayerLocation | null => {
  if (row.venue) {
    const venue = row.venue;
    return {
      id: venue.id,
      name: venue.title,
      address: venue.addressString,
      latitude: venue.latitude != null ? Number(venue.latitude) : null,
      longitude: venue.longitude != null ? Number(venue.longitude) : null,
      timezone: venue.timezone ?? null,
      venue,
    };
  }
  const court = row.userCustomCourt;
  if (!court) return null;
  return {
    id: court.id,
    name: court.title || court.fullAddress,
    address: court.fullAddress,
    latitude: court.latitude != null ? Number(court.latitude) : null,
    longitude: court.longitude != null ? Number(court.longitude) : null,
    timezone: null,
    venue: null,
  };
};

const mapCompetitiveness = (
  competitiveness: EventCompetitivenessEnum | null | undefined,
): PlaySessionMatchCompetitivenessEnum =>
  competitiveness === EventCompetitivenessEnum.Competitive
    ? PlaySessionMatchCompetitivenessEnum.Match
    : PlaySessionMatchCompetitivenessEnum.Casual;

/** A partner-gender preference, or null when the player is open to anyone. */
const mapGenderPreference = (gender: CompetitionGenderEnum): PlayerGender | null => {
  if (gender === CompetitionGenderEnum.Male) return 'MALE';
  if (gender === CompetitionGenderEnum.Female) return 'FEMALE';
  return null;
};

// Slot labels share the ListAvailability formatters (kept exported for existing callers).
export const formatDay = (day: string): string => weekdayShortLabel(day as Weekday);
export const formatTime = formatShortTime;

/** A venue row in the shape every Find query selects it — structural so the host
 *  request queries can reuse this without sharing a generated type. */
export interface FindVenueRow {
  title: string;
  addressString: string;
  accessType?: VenueAccessTypesEnum | null;
  pickleballNets?: VenueNetsEnum | null;
  indoorCourtCount: number;
  outdoorCourtCount: number;
  images?: { url: string }[];
}

export const buildVenueDetails = (venue: FindVenueRow): FindVenueDetails => {
  const netLabel =
    venue.pickleballNets === VenueNetsEnum.Permanent ||
    venue.pickleballNets === VenueNetsEnum.Tennis
      ? 'Permanent'
      : 'Portable';
  return {
    imageUrl: venue.images?.[0]?.url,
    locationLabel: venue.title,
    name: venue.title,
    accessLabel: getAccessParamters(venue.accessType)?.name ?? '',
    courtCount: venue.indoorCourtCount + venue.outdoorCourtCount,
    netLabel,
    fullAddress: venue.addressString,
  };
};

export const mapAvailabilityToPlayer = (
  availability: PlayerAvailabilityRow,
  viewerCenter: LatLng | null,
  searchCenter: LatLng | null,
): FindPlayer => {
  const profile = availability.userProfile;
  const displayName = profile?.fullName || profile?.preferredName || 'Player';
  // Players often have no real handle (a UUID placeholder) — treat that as none so
  // the modal's "@handle" + View-profile row stays hidden.
  const username =
    profile?.username && !isUuidFormatValid(profile.username) ? profile.username : undefined;

  const locations = availability.venues.flatMap((row) => {
    const location = toPlayerLocation(row);
    return location ? [location] : [];
  });
  const locationDistance = (location: PlayerLocation): number | null =>
    distanceMilesTo(viewerCenter, location);
  const venueDistance = (venue: PlayerVenueRow): number | null =>
    distanceMilesTo(viewerCenter, venue);
  // Only venues rows: a custom court can't be the venue a play request is booked at.
  const realVenues = locations.flatMap((location) => (location.venue ? [location.venue] : []));

  const venues: FindPlayerVenue[] = locations.map((location) => ({
    id: location.id,
    name: location.name,
    distanceMiles: locationDistance(location),
    searchDistanceMiles: distanceMilesTo(searchCenter, location),
  }));

  const slots: FindPlayerAvailabilitySlot[] = availability.slots.map((slot) => ({
    id: slot.id,
    day: formatDay(slot.dayOfWeek),
    timeLabel: `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`,
  }));

  const requestVenues: FindPlayerRequestVenue[] = realVenues.map((venue) => ({
    id: venue.id,
    name: venue.title,
    imageUrl: venue.images?.[0]?.url,
    // Ties break to Indoor; a venue with only outdoor courts reads "Outdoor".
    isIndoor: venue.indoorCourtCount >= venue.outdoorCourtCount,
    distanceMiles: venueDistance(venue),
  }));

  const neighborhoods: FindPlayerNeighborhood[] = availability.neighborhoods.map(
    (row) => row.neighborhood,
  );

  // A neighborhood-only listing still needs a map point and a distance.
  const primaryLocation = locations[0];
  const primaryNeighborhood = neighborhoods[0];
  // Slots are wall-clock at the venue; the nearest listed venue is the one the
  // viewer would meet at, so its zone is the one to evaluate "now" in.
  const nearestLocation = locations
    .map((location) => ({ location, distance: locationDistance(location) }))
    .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))[0]?.location;

  // The slot column is a plain string in the schema, but always holds a `Weekday`
  // value (`MON`…) written by the create form.
  const availabilityDays = Array.from(
    new Set(availability.slots.map((slot) => slot.dayOfWeek as Weekday)),
  );
  const availabilityTimes = Array.from(
    new Set(
      availability.slots.map((slot) => timeOfDayFromHour(Number(slot.startTime.split(':')[0]))),
    ),
  );
  const courtTypeSet = new Set<FindCourtType>();
  realVenues.forEach((venue) =>
    deriveCourtTypes(venue.indoorCourtCount, venue.outdoorCourtCount).forEach((ct) =>
      courtTypeSet.add(ct),
    ),
  );

  return {
    id: availability.id,
    // The profile id IS the owner's user id — the play-request recipient.
    targetUserId: profile?.id,
    name: displayName,
    username,
    imageUrl: getProfileImageUrlOrPlaceholder({ path: profile?.profileImagePath }),
    verificationLevel: getVerificationLevel({
      identityVerifications: profile?.identityVerifications,
    }),
    // cityName is often blank; the player's active city is the reliable label.
    city: profile?.activeCity?.name || profile?.cityName || '',
    venues,
    sport: availability.sport,
    competitiveness: mapCompetitiveness(availability.competitiveness),
    teamType: availability.teamType,
    partnerGender: availability.gender,
    skillLevel: availability.selfRatedMinSkillLevel?.id ?? undefined,
    courtTypes: Array.from(courtTypeSet),
    availabilityDays,
    availabilityTimes,
    lookingForSummary: buildGameTitle(availability),
    wantsRegularPartner: availability.wantsRegularPartner,
    genderPreference: mapGenderPreference(availability.gender),
    description: availability.description || undefined,
    availability: slots,
    // A neighborhood-only listing has no venue; its borough's zone stands in.
    timezone: nearestLocation?.timezone ?? primaryNeighborhood?.city.timezone ?? undefined,
    slots: availability.slots.map((slot) => ({
      id: slot.id,
      dayOfWeek: slot.dayOfWeek as Weekday,
      startTime: slot.startTime,
      endTime: slot.endTime,
    })),
    preferredLocations: locations.map((location) =>
      location.venue
        ? buildVenueDetails(location.venue)
        : {
            locationLabel: location.name,
            name: location.name,
            accessLabel: '',
            courtCount: 0,
            netLabel: '',
            fullAddress: location.address,
          },
    ),
    requestVenues,
    neighborhoods,
    latitude:
      primaryLocation?.latitude ??
      (primaryNeighborhood ? Number(primaryNeighborhood.latitude) : undefined),
    longitude:
      primaryLocation?.longitude ??
      (primaryNeighborhood ? Number(primaryNeighborhood.longitude) : undefined),
  };
};

/**
 * Live active player availabilities for the /find "Players" tab, mapped to
 * `FindPlayer`. `areas` (from `searchAreas`) is where to look; `viewerCenter` only
 * drives the per-venue distance and may be null.
 */
export const useFindPlayers = (
  viewerCenter: LatLng | null,
  areas: GeographyComparisonExp[],
  searchCenter: LatLng,
) => {
  // A listing matches when any of its venues, custom courts or neighborhoods is in an area.
  const area = useMemo(
    () => ({
      _or: areas.flatMap((geometry) => [
        { venues: { venue: { geometry } } },
        { venues: { userCustomCourt: { geometry } } },
        { neighborhoods: { neighborhood: { geometry } } },
      ]),
    }),
    [areas],
  );
  // Waiting out the session keeps the first request from going out as `anonymous`
  // only to be re-fired once the viewer resolves.
  const { isSessionLoading } = useViewer();
  const { data, loading } = useGetFindPlayersQuery({
    variables: { area },
    skip: isSessionLoading,
    // Revalidate on mount: a fresh availability must show when its owner returns here.
    fetchPolicy: 'cache-and-network',
  });

  const players = useMemo<FindPlayer[]>(
    () =>
      (data?.playerAvailabilities ?? []).map((availability) =>
        mapAvailabilityToPlayer(availability, viewerCenter, searchCenter),
      ),
    [data, viewerCenter, searchCenter],
  );

  // Not ready = the session hasn't settled, so the query hasn't run. Keep loading
  // rather than flashing an empty list.
  return { players, isLoading: (loading && !data) || isSessionLoading };
};
